/** Generate customer website integration snippets for the train-model predict API. */

export type SnippetLang = "python" | "php" | "javascript";

export type SnippetContext = {
  /** Absolute API origin, e.g. https://tnktrade.ai (no trailing slash). */
  apiBase: string;
  /** Train session id that selects the model. */
  sessionId: string;
  /** Example feature payload (predictor column → number). */
  bodyExample: Record<string, number>;
  /** Optional package label for comments. */
  packageId?: string;
};

const LANGS: { id: SnippetLang; label: string; filename: string }[] = [
  { id: "python", label: "Python", filename: "tnk_model_predict.py" },
  { id: "php", label: "PHP", filename: "tnk_model_predict.php" },
  { id: "javascript", label: "JavaScript", filename: "tnk_model_predict.js" },
];

export function snippetLangOptions() {
  return LANGS;
}

function jsonBody(body: Record<string, number>): string {
  return JSON.stringify(body, null, 2);
}

function sessionPlaceholder(sessionId: string): string {
  return sessionId.trim() || "YOUR_SESSION_ID";
}

function basePlaceholder(apiBase: string): string {
  return (apiBase || "").replace(/\/$/, "") || "https://YOUR_TNK_HOST";
}

export function buildPredictSnippet(lang: SnippetLang, ctx: SnippetContext): string {
  const session = sessionPlaceholder(ctx.sessionId);
  const base = basePlaceholder(ctx.apiBase);
  const body = ctx.bodyExample && Object.keys(ctx.bodyExample).length
    ? ctx.bodyExample
    : { feature: 1.0 };
  const pkgNote = ctx.packageId ? ` (package: ${ctx.packageId})` : "";

  switch (lang) {
    case "python":
      return buildPython(session, base, body, pkgNote);
    case "php":
      return buildPhp(session, base, body, pkgNote);
    case "javascript":
      return buildJavascript(session, base, body, pkgNote);
    default:
      return "";
  }
}

function buildPython(
  session: string,
  base: string,
  body: Record<string, number>,
  pkgNote: string,
): string {
  return `#!/usr/bin/env python3
"""TNK model predict — drop into your website / backend${pkgNote}.

Replace SESSION_ID (and API_BASE if needed), then POST feature values.
Expected result JSON includes: status, prediction, target_feature, interpretation.
"""
from __future__ import annotations

import json
import urllib.error
import urllib.request

# --- replace these placeholders ---
SESSION_ID = "${session}"          # from /models/predict?session=...
API_BASE = "${base}"               # no trailing slash
MODEL_ID = SESSION_ID              # same as session for trained models

FEATURES = ${jsonBody(body)}


def predict(features: dict | None = None) -> dict:
    payload = features if features is not None else FEATURES
    url = f"{API_BASE.rstrip('/')}/api/train/models/{SESSION_ID}/predict"
    req = urllib.request.Request(
        url,
        data=json.dumps(payload).encode("utf-8"),
        headers={"Content-Type": "application/json", "Accept": "application/json"},
        method="POST",
    )
    try:
        with urllib.request.urlopen(req, timeout=30) as resp:
            return json.loads(resp.read().decode("utf-8"))
    except urllib.error.HTTPError as exc:
        detail = exc.read().decode("utf-8", errors="replace")
        raise RuntimeError(f"Predict failed HTTP {exc.code}: {detail}") from exc


if __name__ == "__main__":
    result = predict()
    print(json.dumps(result, indent=2))
    # Expected keys: status, prediction, target_feature, interpretation, confidence
`;
}

function buildPhp(
  session: string,
  base: string,
  body: Record<string, number>,
  pkgNote: string,
): string {
  const phpArray = Object.entries(body)
    .map(([k, v]) => `    '${k.replace(/'/g, "\\'")}' => ${Number(v)},`)
    .join("\n");
  return `<?php
/**
 * TNK model predict — drop into your website / backend${pkgNote}.
 *
 * Replace SESSION_ID (and API_BASE if needed), then POST feature values.
 * Expected result JSON includes: status, prediction, target_feature, interpretation.
 */

// --- replace these placeholders ---
$sessionId = '${session}';          // from /models/predict?session=...
$apiBase   = '${base}';               // no trailing slash
$modelId   = $sessionId;               // same as session for trained models

$features = [
${phpArray}
];

function tnk_model_predict(string $apiBase, string $sessionId, array $features): array
{
    $url = rtrim($apiBase, '/') . '/api/train/models/' . rawurlencode($sessionId) . '/predict';
    $ch = curl_init($url);
    curl_setopt_array($ch, [
        CURLOPT_POST           => true,
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_HTTPHEADER     => [
            'Content-Type: application/json',
            'Accept: application/json',
        ],
        CURLOPT_POSTFIELDS     => json_encode($features),
        CURLOPT_TIMEOUT        => 30,
    ]);
    $raw  = curl_exec($ch);
    $code = (int) curl_getinfo($ch, CURLINFO_HTTP_CODE);
    $err  = curl_error($ch);
    curl_close($ch);

    if ($raw === false) {
        throw new RuntimeException('Predict request failed: ' . $err);
    }
    $data = json_decode($raw, true);
    if (!is_array($data)) {
        throw new RuntimeException('Invalid JSON response (HTTP ' . $code . '): ' . $raw);
    }
    if ($code >= 400) {
        $msg = isset($data['error']) ? (string) $data['error'] : $raw;
        throw new RuntimeException('Predict failed HTTP ' . $code . ': ' . $msg);
    }
    return $data;
}

try {
    $result = tnk_model_predict($apiBase, $sessionId, $features);
    header('Content-Type: application/json');
    echo json_encode($result, JSON_PRETTY_PRINT);
    // Expected keys: status, prediction, target_feature, interpretation, confidence
} catch (Throwable $e) {
    http_response_code(502);
    header('Content-Type: application/json');
    echo json_encode(['status' => 'error', 'error' => $e->getMessage()]);
}
`;
}

function buildJavascript(
  session: string,
  base: string,
  body: Record<string, number>,
  pkgNote: string,
): string {
  return `/**
 * TNK model predict — browser or Node drop-in${pkgNote}.
 *
 * Replace SESSION_ID (and API_BASE if needed), then POST feature values.
 * Expected result JSON includes: status, prediction, target_feature, interpretation.
 */

// --- replace these placeholders ---
const SESSION_ID = "${session}"; // from /models/predict?session=...
const API_BASE = "${base}"; // no trailing slash
const MODEL_ID = SESSION_ID; // same as session for trained models

const FEATURES = ${jsonBody(body)};

/**
 * @param {Record<string, number>} [features]
 * @returns {Promise<{
 *   status?: string,
 *   prediction?: number,
 *   target_feature?: string,
 *   interpretation?: string,
 *   confidence?: number,
 *   error?: string
 * }>}
 */
async function tnkModelPredict(features = FEATURES) {
  const url = \`\${API_BASE.replace(/\\/$/, "")}/api/train/models/\${encodeURIComponent(SESSION_ID)}/predict\`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(features),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status === "error") {
    throw new Error(data.error || data.interpretation || \`Predict failed HTTP \${res.status}\`);
  }
  return data;
}

// Example usage (browser console or Node 18+):
tnkModelPredict()
  .then((result) => {
    console.log(result);
    // Expected keys: status, prediction, target_feature, interpretation, confidence
  })
  .catch((err) => console.error(err));

// Export for bundlers / Node:
if (typeof module !== "undefined" && module.exports) {
  module.exports = { tnkModelPredict, SESSION_ID, MODEL_ID, API_BASE, FEATURES };
}
`;
}

export function downloadSnippet(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function copySnippet(content: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(content);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = content;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}
